import {EntityState} from '@reduxjs/toolkit';

import {IAisCommentsRequestResultTableDtoNd} from 'pages/ais/answer-formation-nd/model/types';
import {TForecastCommentsSliceState} from 'pages/ais/forecast-comments-page';
import {TTaxForecastSliceState} from 'pages/ais/submit-income-tax-forecast';
import {IDateTimeDetails} from 'shared/types/dates';
import {ILookupEntry} from 'shared/types/lookups';
import {TOrganizationStructureDto} from 'store/slices/ais-slice/organization-structure/organization-structure-types';

import {IAisCommentsRequestResultTableDto, IAisInspectorCommentDto} from './ais-comments-request';
import {TFetchAisDashboardDataResponseDto} from './ais-dashboards/ais-dashboards-api';
import {IAisNotificationScenariosSettingsServices, IAisNotificationScenariosSettingsTableDto} from './ais-notification-scenarios-settings';

export type TAisSliceState = {
    registerDocuments: EntityState<IAisRegisterDocumentDto>;
    registerDocumentsSettings: IAisDocRegisterSettings | undefined;
    registerDocumentsFields: IAisDocRegisterFields | undefined;

    /**
     * Значения селекта типа (условия) фильтра по столбцам таблицы в Реестре Документов
     */
    registerDocumentsFilterConditionsEntries?: ILookupEntry[];
    /**
     * Типизация параметров фильтров для Реестра документов
     */
    registerDocumentsColumnsFilters: {
        filters: any; // TTableColumnFilters, нет поддержки рекурсивных структур
        savedFilters: any; // todo: типизировать по аналогии с TableReportColumnSavedFilters
        noFetchWithFilters?: boolean;
        noFetchWithParameters?: boolean;
    };

    registerDocumentsByTax: EntityState<TAisRegisterDocumentByTaxDto>;
    includedDocumentsByTax: EntityState<TAisRegisterDocumentByTaxDto>;

    aisRequirements: EntityState<IAisRequirementDto>;
    aisRequestsToPublish: EntityState<IAisRequestToPublishDto>;
    aisRequests: EntityState<IAisRequestDto>;

    interfaceDocumentsData: EntityState<IAisInterfaceDocumentDto>;
    interfaceDocumentsFields: IAisDocInterfaceFields | undefined;

    meta: {
        documentsTotalCount?: number;
        documentsByTaxTotalCount?: number;
    };

    aisDashboards: {
        availabilityStatus: any;
        summaryTransactionsProtocol: TFetchAisDashboardDataResponseDto | undefined;
        register: TFetchAisDashboardDataResponseDto | undefined;
        s4s5RequestsStatistics: TFetchAisDashboardDataResponseDto | undefined;
        s3s6s12requestStatistic: TFetchAisDashboardDataResponseDto | undefined;
        tokenStatus: any;
        unavailabilityChart: any;
    };

    aisCommentsRequest: {
      aisCommentsRequestTable: IAisCommentsRequestResultTableDto[] | undefined;
      recordsTotal: number | undefined;
      aisInspectorCommentInfo: IAisInspectorCommentDto | undefined;
    };

    organizationStructure: TOrganizationStructureDto | undefined;
    aisAnswerFormationNd: {
        aisCommentsRequestTableNd: IAisCommentsRequestResultTableDtoNd[] | undefined;
        aisInspectorCommentInfoNd: IAisCommentsRequestResultTableDtoNd | undefined;
    };

    aisNotificationScenariosSettings: {
        notificationScenariosTable: IAisNotificationScenariosSettingsTableDto[] | undefined;
        recordsTotal: number | undefined;
        notificationScenariosServices: IAisNotificationScenariosSettingsServices[] | undefined;
    };
} & TTaxForecastSliceState & TForecastCommentsSliceState;

export interface IAisRequestToPublishDto {
    docJournalId: string | null;
    topicId: number;
    requestNum: string | null;
    requestDate: string | null;
    responseDeadline: string | null;
    responseDate: string | null;
    status: string | null;
    responseBy: string | null;
}

export interface IAisRequestDto {
    ticketId: string;
    requestDate: string;
    requestBody: string;
    responseBody: string;
}

export interface IAisRequirementDto {
    requirementNumber: string;
    basisRequirement: string;
    dateReceiptRequirement: string | null;
    responseTime: string | null;
    responseStatus: string | null;
    responseDate: string | null;
    responseBy: string | null;
    responseFTSStatus: string | null;
}

export interface IAisRegisterDocumentDto {
    id: number;
    aisDocId: string;
    baseDocCode: string;
    baseDocDate: string;
    baseDocName: string;
    baseDocNumber: string;
    contentAvailability: number;
    cpartyCountryCode: string;
    cpartyForeignRegNum: string;
    cpartyInn: string;
    cpartyKpp: string;
    cpartyName: string;
    docCode: string;
    docDate: {
        year: number;
        month: string;
        dayOfYear: number;
        monthValue: number;
        dayOfMonth: number;
        chronology: {
            calendarType: string;
            id: string;
        };
        era: string;
        leapYear: boolean;
        dayOfWeek: string;
    };
    docName: string;
    docNumber: string;
    docSumGross: number;
    docTaxSum: number;
    orgId: number;
  }

export interface IAisRegisterTax {
    kndCode: string;
    reportingYear: number;
    taxCode: string;
    corrNum: string;
    taxPeriod: string;
    mappingId: number;

    section: string;
    subsection: string;
}

export interface IAisDocRegisterSettings {
    useParameters: boolean;
    quantityLines: number | undefined;
    contentAvailabilityCondition: boolean;
    useIdAutogeneration: boolean;
}

export interface IAisDocRegisterRawField {
    id: string;
    columnOrdinal: number;
    userName: string;
    field: string;
    fieldType: string;
    display: boolean;
    isParam: boolean;
    paramRequired: boolean;
    paramType: string;
    paramLov: string;
}

export interface IAisDocRegisterField {
    id: string;
    columnOrdinal: number;
    label: string;
    fieldKey: string;
    type: string;
}

export interface IAisDocRegisterFilter extends IAisDocRegisterField {
    isRequired: boolean;
    referenceUrl: string;
}

export interface IAisDocRegisterFields {
    columns: IAisDocRegisterField[] | undefined;
    filters: IAisDocRegisterFilter[] | undefined;
}

export enum EAisDocRegisterFieldTypes {
    DATE = 'date',
    REFERENCE = 'lov',
    MONETARY = 'number(0,00)',
}

export interface IAisDocInterfaceField {
    id: string;
    columnOrdinal: number;
    label: string;
    fieldKey: string;
    type: string;
}

export interface IAisDocInterfaceFilter extends IAisDocRegisterField {
    isRequired: boolean;
    referenceUrl: string;
}

export interface IAisDocInterfaceFields {
    columns: IAisDocInterfaceField[] | undefined;
    filters: IAisDocInterfaceFilter[] | undefined;
}

export enum EAisDocInterfaceFieldTypes {
    DATE = 'date',
    REFERENCE = 'lov',
    MONETARY = 'numeric(19,2)',
}

export interface IAisInterfaceDocumentDto {
    aisDocId: string;
    author: string;
    contentAvailability: number;
    counterpartyCountryCode: string;
    counterpartyForeignRegNumber: string;
    counterpartyInn: string;
    counterpartyKpp: string;
    counterpartyName: string;
    createdById: number;
    createdDate: IDateTimeDetails;
    dateFrom: string;
    dateTo: string;
    docId: number;
    docJournalId: number;
    documentBaseCode: string;
    documentBaseDate: string;
    documentBaseName: string;
    documentBaseNumber: string;
    documentCatCode: string;
    documentCode: string;
    documentDate: string;
    documentName: string;
    documentNumber: string;
    documentSumGross: number;
    documentTaxSum: number;
    editor: string;
    id: number;
    lastModifiedById: number;
    lastModifiedDate: IDateTimeDetails;
    loadId: number;
    orgId: number;
    statusCode: string;
  }

export enum EAisArchiveSystemCode {
    'Showcase' = 'Showcase' // Витрина
}

export type TAisRegisterDocumentByTaxDto = IAisRegisterDocumentDto & IAisRegisterTax;
